import { MeilisearchSearchResponse } from '../types/api';
import { SearchResults } from './types/SearchResults';
import { productNormalizer } from './normalizers/productNormalizer';
import { categorySource } from './categories/categorySource';
import { categoryMatcher } from './categories/categoryMatcher';
import { productVariantsGrouper } from './groupers/productVariantsGrouper';

/**
 * Map raw Meilisearch response to normalized SearchResults
 *
 * Rules:
 * 1. Pure function - no side effects
 * 2. No UI logic, no ranking, no business logic
 * 3. Each hit processed through appropriate normalizer
 * 4. Invalid hits filtered out (null results)
 * 5. Preserve total count and raw response reference
 *
 * @param rawResponse Raw response from Meilisearch (via proxy)
 * @param query Search query string (optional, defaults to rawResponse.query)
 * @returns Normalized SearchResults for UI consumption
 */
export function searchMapper(
  rawResponse: MeilisearchSearchResponse,
  query?: string
): SearchResults {
  // Use explicit query parameter or fall back to rawResponse.query
  const searchQuery = query ?? rawResponse.query;

  // Process each hit through product normalizer
  const normalizedProducts = rawResponse.hits
    .map((hit) => productNormalizer(hit))
    .filter((product): product is NonNullable<typeof product> => product !== null);

  // Group product variants by "Основное наименование"
  const products = productVariantsGrouper(normalizedProducts);

  // Match categories (dev-only feed-based implementation)
  let categories: any[] = [];
  try {
    const allCategories = categorySource.getCategoriesSync();
    console.log('[searchMapper] Categories available:', allCategories.length, 'Query:', searchQuery);

    if (Array.isArray(allCategories) && allCategories.length > 0) {
      categories = categoryMatcher.match(allCategories, searchQuery);
      console.log('[searchMapper] Matched categories:', categories.length);
    } else {
      console.log('[searchMapper] No categories available or empty array');
    }
    // If categories not loaded yet or empty, categories remains empty array
  } catch (error) {
    // Fallback for unexpected errors (dev environment only)
    console.error('[searchMapper] Unexpected error matching categories:', error);
    categories = [];
  }

  // Return normalized results
  return {
    products,
    categories, // Real categories from feed (dev) or empty array if not loaded
    suggestions: [], // Placeholder for future suggestion normalization
    total: rawResponse.estimatedTotalHits,
    raw: rawResponse, // Optional reference to raw response
  };
}